import { Add, Close, Delete } from "@mui/icons-material";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  IconButton,
  Switch,
  FormControlLabel,
  TextField,
  Box,
  Typography,
  Chip,
} from "@mui/material";
import { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { showError, showMessage } from "../../actions/uiActions";
import DomainApi from "../../api/domains";

const ConfigurationModal = ({
  open,
  onClose,
  onSaveSuccess,
  configuration,
  domainId,
  mode = "edit",
}) => {
  const dispatch = useDispatch();
  const isEditMode = mode === "edit";
  const [saving, setSaving] = useState(false);

  const getInitialFormData = () => ({
    entityId: "",
    loginUrl: "",
    logoutUrl: "",
    signerCert: "",
    emailDomain: [],
    mapping: {
      email: "",
      fname: "",
      lname: "",
      employeeId: "",
    },
  });

  const [formData, setFormData] = useState(getInitialFormData());

  const [newEmailDomain, setNewEmailDomain] = useState("");

  useEffect(() => {
    if (isEditMode && configuration) {
      setFormData({
        entityId: configuration.entityId || "",
        loginUrl: configuration.loginUrl || "",
        logoutUrl: configuration.logoutUrl || "",
        signerCert: configuration.signerCert || "",
        emailDomain: configuration.emailDomain || [],
        mapping: {
          email: configuration.mapping?.email || "",
          fname: configuration.mapping?.fname || "",
          lname: configuration.mapping?.lname || "",
          employeeId: configuration.mapping?.employeeId || "",
        },
        status: configuration.status ?? true,
      });
    } else if (!isEditMode) {
      setFormData(getInitialFormData());
    }
  }, [configuration, isEditMode]);

  const handleChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleMappingChange = (field) => (e) => {
    setFormData((prev) => ({
      ...prev,
      mapping: {
        ...prev.mapping,
        [field]: e.target.value,
      },
    }));
  };

  const handleStatusChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      status: e.target.checked,
    }));
  };

  const handleAddEmailDomain = () => {
    if (
      newEmailDomain.trim() &&
      !formData.emailDomain.includes(newEmailDomain.trim())
    ) {
      setFormData((prev) => ({
        ...prev,
        emailDomain: [...prev.emailDomain, newEmailDomain.trim()],
      }));
      setNewEmailDomain("");
    }
  };

  const handleRemoveEmailDomain = (domainToRemove) => {
    setFormData((prev) => ({
      ...prev,
      emailDomain: prev.emailDomain.filter(
        (domain) => domain !== domainToRemove,
      ),
    }));
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddEmailDomain();
    }
  };

  const handleSubmit = async () => {
    setSaving(true);
    try {
      if (isEditMode && configuration?._id) {
        // Edit mode
        await DomainApi.updateSsoConfiguration(configuration._id, formData);
        dispatch(showMessage("Configuration updated successfully"));
        onSaveSuccess?.({ ...configuration, ...formData }, "edit");
      } else {
        // Create mode
        const createPayload = {
          ...formData,
          domainId,
        };
        const result = await DomainApi.createSsoConfiguration(createPayload);
        dispatch(showMessage("Configuration created successfully"));
        onSaveSuccess?.(result, "create");
      }
      onClose();
    } catch (error) {
      dispatch(showError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      aria-labelledby="configuration-dialog"
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle id="configuration-title" sx={{ m: 0, p: 2 }}>
        {isEditMode ? "Edit SSO Configuration" : "Add SSO Configuration"}
      </DialogTitle>
      <IconButton
        aria-label="close"
        onClick={onClose}
        sx={{
          position: "absolute",
          right: 8,
          top: 8,
          color: "black.900",
        }}
      >
        <Close />
      </IconButton>
      <DialogContent
        style={{ display: "flex", flexDirection: "column", gap: 20 }}
      >
        <DialogContentText
          style={{ marginTop: -10, fontSize: 16, marginBottom: 10 }}
        >
          {isEditMode
            ? "Update the SSO configuration details below"
            : "Enter the SSO configuration details below"}
        </DialogContentText>

        {/* Entity ID */}
        <TextField
          label="Entity ID"
          fullWidth
          value={formData.entityId}
          onChange={handleChange("entityId")}
          variant="outlined"
          size="small"
        />

        {/* Login URL */}
        <TextField
          label="Login URL"
          fullWidth
          value={formData.loginUrl}
          onChange={handleChange("loginUrl")}
          variant="outlined"
          size="small"
        />

        {/* Logout URL */}
        <TextField
          label="Logout URL"
          fullWidth
          value={formData.logoutUrl}
          onChange={handleChange("logoutUrl")}
          variant="outlined"
          size="small"
        />

        {/* Signer Certificate */}
        <TextField
          label="Signer Certificate"
          fullWidth
          multiline
          rows={4}
          value={formData.signerCert}
          onChange={handleChange("signerCert")}
          variant="outlined"
          size="small"
        />

        {/* Email Domains */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            Email Domains
          </Typography>
          <Box display="flex" gap={1} mb={1}>
            <TextField
              label="Add email domain"
              value={newEmailDomain}
              onChange={(e) => setNewEmailDomain(e.target.value)}
              onKeyDown={handleKeyPress}
              variant="outlined"
              size="small"
              fullWidth
              placeholder="e.g., example.com"
            />
            <Button
              variant="contained"
              onClick={handleAddEmailDomain}
              disabled={!newEmailDomain.trim()}
              sx={{ minWidth: "auto", px: 2 }}
            >
              <Add />
            </Button>
          </Box>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {formData.emailDomain.map((domain, index) => (
              <Chip
                key={index}
                label={domain}
                onDelete={() => handleRemoveEmailDomain(domain)}
                deleteIcon={<Delete />}
                color="primary"
                variant="outlined"
              />
            ))}
          </Box>
        </Box>

        <Divider />

        {/* Mapping Section */}
        <Box>
          <Typography variant="subtitle2" gutterBottom>
            SSO Attribute Mapping
          </Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <TextField
              label="Email Mapping"
              fullWidth
              value={formData.mapping.email}
              onChange={handleMappingChange("email")}
              variant="outlined"
              size="small"
              placeholder="e.g., http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"
            />
            <TextField
              label="First Name Mapping"
              fullWidth
              value={formData.mapping.fname}
              onChange={handleMappingChange("fname")}
              variant="outlined"
              size="small"
              placeholder="e.g., http://schemas.microsoft.com/identity/claims/displayname"
            />
            <TextField
              label="Last Name Mapping"
              fullWidth
              value={formData.mapping.lname}
              onChange={handleMappingChange("lname")}
              variant="outlined"
              size="small"
              placeholder="e.g., http://schemas.microsoft.com/identity/claims/objectidentifier"
            />
            <TextField
              label="Employee ID Mapping"
              fullWidth
              value={formData.mapping.employeeId}
              onChange={handleMappingChange("employeeId")}
              variant="outlined"
              size="small"
              placeholder="e.g., http://schemas.xmlsoap.org/ws/2005/05/identity/claims/employeeid"
            />
          </Box>
        </Box>

        <Divider />

        {/* Status Toggle - Only show in edit mode */}
        {isEditMode && (
          <div>
            <FormControlLabel
              control={
                <Switch
                  checked={formData.status}
                  onChange={handleStatusChange}
                  color="primary"
                />
              }
              label={formData.status ? "Enabled" : "Disabled"}
            />
            {!formData.status && (
              <p
                style={{
                  fontSize: "12px",
                  color: "#666",
                  marginTop: "-8px",
                  marginLeft: "48px",
                }}
              >
                Users for this domain will log in with standard email/password
                (no SSO).
              </p>
            )}
          </div>
        )}
      </DialogContent>
      <Divider />

      <DialogActions style={{ justifyContent: "space-between", padding: 20 }}>
        <Button onClick={onClose} variant="outlined" color="warning">
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          color="primary"
          disabled={saving}
        >
          {saving
            ? "Saving..."
            : isEditMode
              ? "Save Changes"
              : "Create Configuration"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfigurationModal;
